import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";

/**
 * The categories an event can be tagged with (`work`, `social`, `exercise`,
 * …).
 */
export type EventCategory =
  | "work"
  | "social"
  | "exercise"
  | "transportation"
  | "food"
  | "shower"
  | "errands"
  | "nap"
  | "creative"
  | "break";

/** Values that go with `EventCategory`: `ALL`, `parse`. */
export namespace EventCategory {
  /** Every category, in display order. */
  export const ALL: readonly EventCategory[] = Object.freeze([
    "work",
    "social",
    "exercise",
    "transportation",
    "food",
    "shower",
    "errands",
    "nap",
    "creative",
    "break",
  ]);

  /**
   * `raw` (an event's wire `category` / `suggestedCategory`) as a category:
   * `null` when it names none — an untagged event, or a name outside `ALL`.
   */
  export function parse(raw: Option<string>): Option<EventCategory> {
    return raw == null ? null : ALL.find((c) => c === raw) ?? null;
  }
}

/**
 * Theme interface for resolving semantic colors.
 * A host implements it with its own theme.
 */
export interface SemanticTheme {
  /** The colour `domain`/`name` names in `appearance`; `null` when the theme names none. */
  semanticColor(domain: string, name: string, appearance: "dark" | "light"): Option<string>;
  /** The dimmed colour `domain`/`name` names in `appearance`; `null` when the theme names none. */
  semanticColorDimmed(
    domain: string,
    name: string,
    appearance: "dark" | "light",
  ): Option<string>;
}

/**
 * How a client's event categories look (`ClientConfig.appearance`, read back
 * as `client.events.categories.appearance()`): the theme their colours
 * resolve in, whether the page shows dark, and the host platform, which picks
 * the category glyph set. Required, with no default: the browser app passes
 * its theme store and dark-mode reads; a headless host names
 * `Appearance.HEADLESS`. `theme` and `isDark` are read on every colour
 * lookup, so a theme or mode switch shows on the next paint.
 */
export type Appearance = {
  /** The theme the category colours resolve in; `null` when none is set. */
  readonly theme: () => Option<SemanticTheme>;
  /** Whether the page shows dark (the theme lookups' `"dark"` / `"light"`). */
  readonly isDark: () => boolean;
  /** The host platform: `"windows"` swaps in the glyphs that platform draws. */
  readonly platform: "windows" | "other";
};

/** Values that go with `Appearance`: `HEADLESS`, `mode`. */
export namespace Appearance {
  /**
   * No theme, light, the default glyphs — for a host with no page (a CLI, a
   * server, a test): every category colour reads its fallback.
   */
  export const HEADLESS: Appearance = Object.freeze({
    theme: () => null,
    isDark: () => false,
    platform: "other",
  });

  /** `appearance`'s current mode, as the theme lookups take it. */
  export function mode(appearance: Appearance): "dark" | "light" {
    return appearance.isDark() ? "dark" : "light";
  }
}

/**
 * One event category with the emoji it is shown with, as a client's
 * category table (`client.events.categories`, `EventCategories`) hands it
 * out: its colours resolve in that client's appearance.
 */
export class EventCategoryItem {
  readonly #appearance: Appearance;

  /** `appearance`: the table's (`EventCategories`), which colours resolve in. */
  constructor(
    readonly name: EventCategory,
    readonly emoji: string,
    appearance: Appearance,
  ) {
    this.#appearance = appearance;
  }

  /** This category's colour in its client's theme; `null` when it names none. */
  colorOpt(): Option<string> {
    return this.colorOptFor(this.#appearance.theme());
  }

  /** This category's colour in `theme`; `null` for no theme or when it names none. */
  colorOptFor(theme: Option<SemanticTheme>): Option<string> {
    return theme?.semanticColor("events", this.name, Appearance.mode(this.#appearance)) ?? null;
  }

  /** This category's dimmed colour in its client's theme; `null` when it names none. */
  colorDimmedOpt(): Option<string> {
    return this.colorDimmedOptFor(this.#appearance.theme());
  }

  /** This category's dimmed colour in `theme`; `null` for no theme or when it names none. */
  colorDimmedOptFor(theme: Option<SemanticTheme>): Option<string> {
    return theme?.semanticColorDimmed(
      "events",
      this.name,
      Appearance.mode(this.#appearance),
    ) ?? null;
  }
}

/** One category of the catalog, with the glyph a Windows host draws when it differs. */
type CategoryGlyphs = {
  readonly name: EventCategory;
  readonly emoji: string;
  readonly emojiWindows: Option<string>;
};

/** The category catalog, in display order. */
const CATALOG: readonly CategoryGlyphs[] = Object.freeze([
  { name: "work", emoji: "🧑‍💻", emojiWindows: "💻" },
  { name: "social", emoji: "👋", emojiWindows: null },
  { name: "exercise", emoji: "💪", emojiWindows: null },
  { name: "transportation", emoji: "🚙", emojiWindows: null },
  { name: "food", emoji: "🍲", emojiWindows: null },
  { name: "shower", emoji: "🚿", emojiWindows: null },
  { name: "errands", emoji: "🛒", emojiWindows: null },
  { name: "nap", emoji: "😴", emojiWindows: null },
  { name: "creative", emoji: "🎨", emojiWindows: null },
  { name: "break", emoji: "☕", emojiWindows: null },
]);

/**
 * A client's event categories (`client.events.categories`): every category
 * as an `EventCategoryItem` whose colours resolve in the client's
 * `ClientConfig.appearance` and whose glyph is its platform's. One table per
 * client, so two clients in a process (a page and a preview) never read each
 * other's theme. A host with no client (a component gallery) builds its own
 * with `new EventCategories(appearance)`.
 */
export class EventCategories {
  /** Every category name, in display order (no appearance needed). */
  static readonly NAMES: readonly EventCategory[] = Object.freeze(CATALOG.map((c) => c.name));

  readonly #appearance: Appearance;
  readonly #items: readonly EventCategoryItem[];
  readonly #byName: ReadonlyMap<EventCategory, EventCategoryItem>;

  constructor(appearance: Appearance) {
    this.#appearance = appearance;
    const windows = appearance.platform === "windows";
    this.#items = Object.freeze(
      CATALOG.map(({ name, emoji, emojiWindows }) =>
        new EventCategoryItem(name, windows && emojiWindows != null ? emojiWindows : emoji, appearance)
      ),
    );
    this.#byName = new Map(this.#items.map((item) => [item.name, item]));
  }

  /** The appearance this table's colours and glyphs resolve in (`ClientConfig.appearance`). */
  appearance(): Appearance {
    return this.#appearance;
  }

  /** Every category, in display order. */
  all(): readonly EventCategoryItem[] {
    return this.#items;
  }

  /** The category `name`; `null` for `null`. */
  itemOpt(name: Option<EventCategory>): Option<EventCategoryItem> {
    if (name == null) return null;
    return this.#byName.get(name) ?? null;
  }

  /** The category `name`. */
  item(name: EventCategory): EventCategoryItem {
    return nonnull(this.#byName.get(name));
  }
}
